/**
 * Proof Dialog Component
 * 
 * Create/edit proof items with title, description, URL, tags, and category.
 * Per design.md: Premium UI, semantic tokens, lucide icons.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc, Id } from 'convex/_generated/dataModel';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    Button, Input, Skeleton,
} from '@uprosal/ui';
import { Textarea } from '@uprosal/ui';

interface ProofDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    proof: Doc<'proofItems'> | null;
}

const CATEGORIES = [
    { value: 'portfolio', label: 'Portfolio' },
    { value: 'testimonial', label: 'Testimonial' },
    { value: 'case_study', label: 'Case Study' },
    { value: 'certification', label: 'Certification' },
];

export function ProofDialog({ open, onOpenChange, proof }: ProofDialogProps) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [url, setUrl] = useState('');
    const [tagsInput, setTagsInput] = useState('');
    const [category, setCategory] = useState('portfolio');
    const [profileId, setProfileId] = useState<Id<'connectedProfiles'> | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const profiles = useQuery(api.queries.profiles.listConnected, {}) as Doc<'connectedProfiles'>[] | undefined;
    const createProof = useMutation(api.mutations.proofItems.create);
    const updateProof = useMutation(api.mutations.proofItems.update);

    // Initialize form when editing
    useEffect(() => {
        if (proof) {
            setTitle(proof.title);
            setDescription(proof.description);
            setUrl(proof.url || '');
            setTagsInput(proof.tags.join(', '));
            setCategory(proof.category || 'portfolio');
            setProfileId(proof.profileId);
        } else {
            setTitle('');
            setDescription('');
            setUrl('');
            setTagsInput('');
            setCategory('portfolio');
            setProfileId(undefined);
        }
        setError('');
    }, [proof, open]);

    const handleSubmit = async () => {
        if (!title.trim()) {
            setError('Title is required');
            return;
        }
        if (!description.trim()) {
            setError('Description is required');
            return;
        }

        setIsSubmitting(true);
        setError('');

        const tags = tagsInput
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length > 0);

        try {
            if (proof) {
                await updateProof({
                    proofId: proof._id,
                    title: title.trim(),
                    description: description.trim(),
                    url: url.trim() || undefined,
                    tags,
                    category,
                    profileId,
                });
            } else {
                await createProof({
                    title: title.trim(),
                    description: description.trim(),
                    url: url.trim() || undefined,
                    tags,
                    category,
                    profileId,
                });
            }
            onOpenChange(false);
        } catch (err) {
            console.error('Failed to save proof item:', err);
            setError('Failed to save. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[600px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {proof ? 'Edit Proof Item' : 'Add Proof Item'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Title */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Title *
                        </label>
                        <Input
                            placeholder="e.g., WordPress E-commerce Portfolio"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Description *
                        </label>
                        <Textarea
                            placeholder="Describe this proof item - what makes it relevant for proposals?"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                        />
                    </div>

                    {/* URL */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            URL (optional)
                        </label>
                        <Input
                            placeholder="https://..."
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                        />
                    </div>

                    {/* Tags */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Tags
                        </label>
                        <Input
                            placeholder="wordpress, ecommerce, woocommerce (comma separated)"
                            value={tagsInput}
                            onChange={(e) => setTagsInput(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                            Tags help AI match this proof to relevant jobs
                        </p>
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Category
                        </label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                            {CATEGORIES.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Profile binding */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Bind to Profile (optional)
                        </label>
                        {profiles === undefined ? (
                            <Skeleton className="h-10 w-full" />
                        ) : (
                            <select
                                value={profileId || ''}
                                onChange={(e) => setProfileId(e.target.value as Id<'connectedProfiles'> || undefined)}
                                className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                <option value="">All Profiles</option>
                                {profiles.map((profile) => (
                                    <option key={profile._id} value={profile._id}>
                                        {profile.profileLabel}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Error message */}
                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}
                </div>

                <DialogFooter className="mt-6 gap-3">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : (proof ? 'Save Changes' : 'Add Proof')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
